import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Service, Container, history } from '../../../symphony';

import { SlLEContext } from '../global-constants';
import { SlDataService } from './sl-data.service';

export interface INavigation {
  courseAlias?: string;
  pathAlias?: string;
  materialRelationId?: number;
  lessonPage?: number;
  toMainPage?: boolean;
  toLEPage?: boolean; // course or path
  toNotFoundCourse?: boolean;
  toNotFoundLesson?: boolean;
  toErrorRoute?: boolean;
  isSkipAhead?: boolean;
  query?: string;
  options?: {
    [key: string]: boolean | string;
  };
}

@Service()
export class SlNavigationService {
  private currentNavigationConfig: INavigation;

  private splittedPath = (): string[] => window.location.pathname.split('/');

  private mainRoute = (): string => `/${this.splittedPath()[2]}`;

  private courseAliasPrefix = (): string => {
    const { routeData$ } = Container.take(SlLEContext, SlDataService);
    const splittedPath = this.splittedPath();
    return (
      routeData$.value?.courseAlias
      || (splittedPath[3] === 'courses' ? splittedPath[4] : null)
    );
  };

  private pathAliasPrefix = (): string => {
    const { routeData$ } = Container.take(SlLEContext, SlDataService);
    const splittedPath = this.splittedPath();
    return (
      routeData$.value?.pathAlias
      || (splittedPath[3] === 'learning-paths' ? splittedPath[4] : null)
    );
  };

  private createNavigationConfig = (config: INavigation): INavigation => ({
    courseAlias:
      config.courseAlias
      || this.currentNavigationConfig?.courseAlias
      || this.courseAliasPrefix(),
    pathAlias:
      config.pathAlias
      || this.currentNavigationConfig?.pathAlias
      || this.pathAliasPrefix(),
    materialRelationId:
      config.materialRelationId
      || this.currentNavigationConfig?.materialRelationId
      || Number(this.splittedPath()[5]),
  });

  private constructRoute = (
    received: INavigation,
    processed: INavigation,
    isSkipAhead?: boolean,
  ): string => {
    const splittedPath = this.splittedPath();
    if (received.materialRelationId) {
      return `/${splittedPath[2]}/${splittedPath[3]}/${splittedPath[3] === 'courses'
        ? processed.courseAlias
        : processed.pathAlias
        }/${isSkipAhead ? 'skip-ahead' : 'lesson'}/${processed.materialRelationId}`;
    }
    if (received.courseAlias) {
      return `/${splittedPath[2]}/${splittedPath[3]}/${processed.courseAlias}`;
    }
    if (received.pathAlias) {
      return `/${splittedPath[2]}/${splittedPath[3]}/${processed.pathAlias}`;
    }
    if (received.toMainPage) {
      return this.mainRoute();
    }
    if (received.toLEPage) {
      return `/${splittedPath[2]}/${splittedPath[3]}/${splittedPath[4]}`;
    }
    if (received.toNotFoundCourse) {
      return `${this.mainRoute()}/not-found-course`;
    }
    if (received.toNotFoundLesson) {
      return `${this.mainRoute()}/not-found-lesson`;
    }
    if (received.toErrorRoute) {
      return '/error';
    }
    return window.location.pathname;
  };

  public loading$ = new BehaviorSubject<boolean>(false);

  public unlockLoading$ = new BehaviorSubject<boolean>(false);

  public navigation$ = new BehaviorSubject<INavigation>(null);

  constructor() {
    const unsubscriber: Subject<void> = Container.take(
      SlLEContext,
      'unsubscriber',
    ) || new Subject<void>();
    this.navigation$
      .pipe(
        filter((nav) => nav !== null),
        takeUntil(unsubscriber),
      )
      .subscribe((nav: INavigation) => {
        const newNavigationConfig = this.createNavigationConfig(nav);
        this.currentNavigationConfig = newNavigationConfig;
        const newRoute: string = this.constructRoute(
          nav,
          newNavigationConfig,
          nav?.isSkipAhead,
        );

        // TODO: add case if there is materialRelationId but dont have query params, make it 0
        if (nav.options?.replace) {
          history.replace(
            nav.query ? newRoute + nav.query : newRoute,
          );
        } else {
          history.push(nav.query ? newRoute + nav.query : newRoute);
        }
      });
  }
}
